import { selectHdAlu } from "@/stores/actions/selectHdAlu";
import { $selectedImage } from "@/stores/selectedImage";
import { $visualizer, visualizerService } from "@/stores/visualizer";
import { PRODUCT_TYPE, PRODUCT_TYPE_PROPS } from "@/utils/content/productTypes";
import { LineItem } from "@/utils/types/types";
import { isMountOnlyTreatment, mountOnlyReveal, type MountOnlySpec } from "@/utils/content/mountOnly";

/** Staff mount-only line (mountOnly.ts): rebuild the options' spec from the Create-Item fields the line carries. */
export const mountOnlyFromItemJson = (itemJson: any): MountOnlySpec | null => {
  if (!itemJson || !isMountOnlyTreatment(itemJson?.treatmentOptions?.type)) return null;
  if (String(itemJson.productType || "").match(/framed|canvas|aluminum/i)) return null;
  return {
    substrate: String(itemJson.mountSubstrate || "sntra"),
    brace: !!itemJson.brace,
    cleat: !!itemJson.cleat,
    frame: String(itemJson.frameOptions?.label || "").trim(),
    reveal: mountOnlyReveal(itemJson.floaterReveal), // lines saved before 10-13 carry none → 1/4"
  };
};


// Staff custom (special-order) moulding: the saved line's label is the custom text; put the
// stand-in frame's label back so the frame renders and FramingPanel's restore finds it in
// FRAMES. customMoulding stays on the options so the checkbox comes back on (customMoulding.ts).
const restoreStandInFrame = (frameOptions: any) => {
  const standIn = frameOptions?.customMoulding?.standIn;
  return standIn ? { ...frameOptions, label: standIn } : frameOptions;
};
/**
 * Restores the visualizer state from a saved LineItem
 * Used when editing an existing cart item
 */
export async function restoreVisualizerFromLineItem(lineItem: LineItem) {
  const itemJson = lineItem.itemJson as any; // Use 'any' to access dynamic properties

  // Map product type string to enum
  let productType: (typeof PRODUCT_TYPE)[keyof typeof PRODUCT_TYPE];
  if (itemJson.productType.match(/canvas/i)) {
    productType = PRODUCT_TYPE.CANVAS;
  } else if (itemJson.productType.match(/aluminum/i)) {
    productType = PRODUCT_TYPE.HD_ALUMINUM;
  } else {
    productType = PRODUCT_TYPE.GICLEE;
  }

  // Load the image (prefer compressed, then original, then cropped as fallback)
  const displayUrl =
    itemJson.compressedImageUrl ||
    itemJson.imageUrl ||
    itemJson.croppedImageUrl;
  const imageName = itemJson.fileName || "image";

  const dimensionSourceUrl = itemJson.rawImageUrl || displayUrl;

  // Extract dimensions from saved sizing (this is crucial for proper restoration)
  const savedPrintWidth = itemJson.sizing?.dimensions?.width || 0;
  const savedPrintHeight = itemJson.sizing?.dimensions?.height || 0;
  const savedRealWidth = itemJson.realWidth || savedPrintWidth;
  const savedRealHeight = itemJson.realHeight || savedPrintHeight;
  console.info("[restoreVisualizerFromLineItem] Restoring item", {
    lineItemId: lineItem.id,
    savedSize: { width: savedPrintWidth, height: savedPrintHeight },
    savedReal: { width: savedRealWidth, height: savedRealHeight },
    displayUrl,
    dimensionSourceUrl,
  });

  const estimatedPixelWidth =
    savedRealWidth > 0 ? (savedRealWidth * 300) / 12 : 3000;
  const estimatedPixelHeight =
    savedRealHeight > 0 ? (savedRealHeight * 300) / 12 : 3000;

  function applyVisualizerState() {
    const visualizer = $visualizer.get();
    const productTypeLabel = itemJson.productType || "";
    const treatmentType = itemJson.treatmentOptions?.type || "";
    const hasFraming = productTypeLabel.match(/framed/giu)
      ? true
      : productTypeLabel.match(/fine art/giu)
        ? false
        : !!treatmentType.match(
            /(frame|floater|matted|shadow|float|straight)/giu
          );

    $visualizer.set({
      ...visualizer,
      // A "previously purchased print" line stores the reduced price; restore
      // the FULL price so the deduction is derived once, not compounded.
      currPricing:
        ((itemJson.prevPrinted || itemJson.multiWindow) && itemJson.fullPrice?.toString?.()) ||
        itemJson.price?.toString?.() ||
        visualizer.currPricing,
      prevPrinted: !!itemJson.prevPrinted,
      prevPrintedOrder: String(itemJson.prevPrintedOrder || ""),
      suppliedArt: !!itemJson.supplied,
      // Multi-window mat (stores/multiWindow.ts): the box and the ticked cart lines come back on Edit.
      multiWindow: !!itemJson.multiWindow,
      multiWindowLineIds: Array.isArray(itemJson.multiWindow?.prints)
        ? itemJson.multiWindow.prints.map((p: any) => String(p?.lineItemId || "")).filter(Boolean)
        : [],
      // The staffer's name for a supplied piece was saved as its file name (createLineItem 09-22).
      suppliedTitle:
        itemJson.supplied && itemJson.fileName && !/CustomerSuppliedArt/i.test(String(itemJson.fileName))
          ? String(itemJson.fileName)
          : "",
      selectedProduct: productType,
      selectedSize: itemJson.sizing || visualizer.selectedSize,
      defaultSize: itemJson.sizing || visualizer.defaultSize,
      options: {
        display: hasFraming,
        // Merge over the product's default frame. Print-only / mat-only lines
        // are saved with a ZEROED frame (no label/texture — the 07-22
        // phantom-frame fix), and the 3D pipeline derives frameType from
        // frameOptions.label: "" → frameUpdater throws "no frame materials"
        // → blank canvas on Edit (staging 2026-09-03). The default frame is
        // exactly what a fresh visualizer carries while display is false.
        frameOptions: hasFraming
          ? restoreStandInFrame(
              itemJson.frameOptions || visualizer.options.frameOptions || {}
            )
          : {
              // Unframed: the saved frame is zeroed (face 0 / depth 0 —
              // degenerate geometry). Use the default hidden frame and keep
              // only the line's matboard, which is all that fix preserved.
              ...((PRODUCT_TYPE_PROPS as any)[productType]?.options
                ?.frameOptions || {}),
              ...(itemJson.frameOptions?.matboard
                ? { matboard: itemJson.frameOptions.matboard }
                : {}),
            },
        paperOptions:
          itemJson.paperOptions || visualizer.options.paperOptions || {},
        treatmentOptions:
          itemJson.treatmentOptions ||
          visualizer.options.treatmentOptions ||
          {},
        finish: itemJson.type || visualizer.options.finish,
        mountOnly: mountOnlyFromItemJson(itemJson),
      },
      selectedRenderView: visualizer.selectedRenderView,
      cropActive: false,
      imageCrop: itemJson.croppedImageUrl
        ? {
            url: itemJson.croppedImageUrl,
            // Keep the full-res crop rectangle through Edit → Update Item.
            ...(itemJson.cropRect ? { rect: itemJson.cropRect } : {}),
          }
        : {},
      forceUpdate: visualizer.forceUpdate + 1,
    });

    if (productType === PRODUCT_TYPE.HD_ALUMINUM && itemJson.type) {
      selectHdAlu({ finish: itemJson.type });
    }
  }

  // Set image and visualizer state before selectProduct so pricing panels
  // (e.g. PaperPanel) don't crash while the source image is still loading.
  $selectedImage.set({
    name: imageName,
    url: displayUrl,
    rawUrl: itemJson.rawImageUrl || displayUrl,
    thumbnailUrl: displayUrl,
    pixelHeight: estimatedPixelHeight,
    pixelWidth: estimatedPixelWidth,
    realHeight: savedRealHeight,
    realWidth: savedRealWidth,
    resolution: 0,
    updatedAt: new Date().toISOString(),
  });
  applyVisualizerState();
  visualizerService.selectProduct(productType);

  // Refine pixel dimensions once the source image has loaded.
  return new Promise<void>(resolve => {
    const img = new Image();
    let done = false;

    const finish = () => {
      if (done) return;
      done = true;

      const currentImage = $selectedImage.get();
      if (currentImage && img.naturalWidth) {
        $selectedImage.set({
          ...currentImage,
          pixelHeight: img.naturalHeight,
          pixelWidth: img.naturalWidth,
        });
      }
      resolve();
    };

    img.onload = finish;

    img.onerror = () => {
      if (done) return;
      done = true;
      resolve();
    };

    img.src = dimensionSourceUrl;

    // Safari may not fire load/error for images served from cache, so check
    // for an already-complete image synchronously.
    if (img.complete) finish();

    // Hard fallback: never let the visualizer hang waiting on an image event
    // that doesn't fire (e.g. Safari + cross-origin shared-cart images).
    // Falls back to the estimated pixel dimensions computed above.
    setTimeout(() => {
      if (done) return;
      done = true;
      resolve();
    }, 8000);
  });
}
